/**
 * staticMotionVectors — honest motion vectors for STATIC instanced / batched
 * geometry (2026-09-12, "the trees and foliage act weird even when I am not
 * moving" with motion blur on).
 *
 * three r185's velocity path for an InstancedMesh (nodes/accessors/Instance.js)
 * keeps the PREVIOUS frame's instance matrices in a cloned attribute. For a
 * mesh above the uniform-buffer limit (1,024 instances on WebGPU, 256 on
 * WebGL2) that clone is an instanced vertex attribute; `OnObjectUpdate`
 * refreshes its CPU array every frame (`array.set`) but never bumps the
 * attribute's version, so the GPU copy stays at whatever the buffer held when
 * the shader was first built. Every slot filled or reused afterwards — a
 * streamed tree, a re-aimed impostor, a swap-removed pool slot — then reads a
 * velocity equal to its distance from that stale matrix: metre-long streaks
 * on a still camera. BatchedMesh (Batch.js) never transforms
 * `positionPrevious` at all, so a batched instance's "previous" is its
 * un-placed geometry.
 *
 * The fix rides NodeMaterial.setupPosition's order — instancing / batching
 * first, the material's `positionNode` LAST: assign `positionPrevious` from
 * the CURRENT instanced `positionLocal`, so a static instance carries zero
 * object velocity (the camera's own motion still blurs it through the
 * previous / current view matrices). Materials that already own a
 * positionNode are left alone: they must assign positionPrevious themselves
 * (the positionPrevious LAW — HierarchicalWind, applyInfluenceToFoliage, the
 * Atlas wind do). Safe on a plain mesh sharing the material: its previous is
 * still resolved through three's per-object previous world matrix.
 */

import type * as THREE from 'three/webgpu';
import { Fn, positionLocal, positionPrevious } from 'three/tsl';

type PositionMaterial = THREE.Material & { positionNode?: unknown; userData: Record<string, unknown> };

/** userData key marking a material already pinned (idempotent, survives clones). */
export const STATIC_MOTION_VECTORS_KEY = 'staticMotionVectors';

/**
 * Pin a material's motion vectors to "static": returns true when the material
 * is pinned (now or before), false when it owns a positionNode and was left
 * to its own previous-position discipline.
 */
export function pinStaticMotionVectors(material: THREE.Material): boolean {
  const m = material as PositionMaterial;
  if (m.userData?.[STATIC_MOTION_VECTORS_KEY] === true) return true;
  if (m.positionNode) return false;
  // Probe lever: `globalThis.__disableStaticMotionVectors = true` before the
  // pools build reproduces the un-pinned streaks (the before/after picture).
  if ((globalThis as { __disableStaticMotionVectors?: boolean }).__disableStaticMotionVectors === true) return false;
  m.positionNode = Fn(() => {
    positionPrevious.assign(positionLocal);
    return positionLocal;
  })();
  if (!m.userData) m.userData = {};
  m.userData[STATIC_MOTION_VECTORS_KEY] = true;
  m.needsUpdate = true;
  return true;
}
